import React from 'react';
import { useApp } from '../../context/AppContext';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import { HOME_KPIS, HOME_FEED, HOME_ACTIONS, HOME_ACTIVE_PLAYBOOKS } from '../../constants';

const KPI_ACCENTS = ['#6366F1', '#D4A042', '#EF4444', '#22C55E'];

export default function HomeScreen() {
  const { currentUser, go, setActivePbIdx } = useApp();
  const { isMobile, rp } = useBreakpoint();
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const greetingEmoji = hour < 12 ? '☀️' : hour < 17 ? '👋' : '🌙';
  const firstName = currentUser?.name?.split(' ')[0] ?? '';
  const px = rp({ mobile: '16px', tablet: '22px', desktop: '28px' });

  return (
    <div style={{ flex: 1, overflowY: 'auto', height: '100%', background: 'var(--surface)' }}>

      {/* ── Page header ── */}
      <div style={{
        padding: `${rp({ mobile: '20px', desktop: '26px' })} ${px} 0`,
        display: 'flex', alignItems: 'flex-start',
        justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap',
      }}>
        <div>
          <div style={{
            fontSize: rp({ mobile: '20px', desktop: '24px' }),
            fontWeight: '800', color: 'var(--text)',
            fontFamily: "'DM Serif Display', Georgia, serif",
            letterSpacing: '-.3px', lineHeight: 1.2,
          }}>
            {greeting}, {firstName} {greetingEmoji}
          </div>
          <div style={{ fontSize: '13px', color: 'var(--text3)', marginTop: '5px', fontWeight: '400' }}>
            {isMobile
              ? "Here's your sales summary"
              : "Here's what needs your attention today"}
          </div>
        </div>
        {!isMobile && (
          <button
            onClick={() => { setActivePbIdx(0); go('client'); }}
            style={{
              display: 'flex', alignItems: 'center', gap: '7px',
              padding: '9px 16px', borderRadius: '10px',
              background: 'var(--navy)', border: 'none',
              color: 'rgba(255,255,255,.9)', fontFamily: 'inherit',
              fontSize: '12.5px', fontWeight: '600', cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(12,25,41,.25)',
              transition: 'transform .15s, box-shadow .15s',
              flexShrink: 0,
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(12,25,41,.3)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = '0 2px 8px rgba(12,25,41,.25)'; }}
          >
            <i className="ti ti-eye" style={{ fontSize: '15px', color: 'var(--gold2)' }} />
            Buyer view
          </button>
        )}
      </div>

      <div style={{ padding: `18px ${px} 24px` }}>

        {/* ── KPI strip ── */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: rp({ mobile: '1fr 1fr', desktop: 'repeat(4,1fr)' }),
          gap: '12px', marginBottom: '18px',
        }}>
          {HOME_KPIS.map((kpi, i) => (
            <div key={i} style={{
              background: 'var(--card-bg)',
              border: '1px solid var(--border)',
              borderRadius: '14px',
              padding: rp({ mobile: '14px 14px 12px', desktop: '16px 18px 14px' }),
              boxShadow: 'var(--shadow-sm)',
              position: 'relative', overflow: 'hidden',
              transition: 'transform .18s, box-shadow .18s',
              cursor: 'default',
            }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = 'var(--shadow-md)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = 'var(--shadow-sm)'; }}
            >
              {/* Accent stripe */}
              <div style={{
                position: 'absolute', top: 0, left: 0, right: 0,
                height: '3px', background: KPI_ACCENTS[i],
                borderRadius: '14px 14px 0 0',
              }} />
              {/* Subtle background glow */}
              <div style={{
                position: 'absolute', top: '-20px', right: '-10px',
                width: '80px', height: '80px', borderRadius: '50%',
                background: KPI_ACCENTS[i] + '0C',
                pointerEvents: 'none',
              }} />
              <div style={{ fontSize: '10px', fontWeight: '600', color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: '8px' }}>
                {kpi.label}
              </div>
              <div style={{
                fontSize: rp({ mobile: '26px', desktop: '30px' }),
                fontWeight: '800', color: kpi.valueColor || 'var(--text)',
                lineHeight: 1, letterSpacing: '-.5px',
              }}>
                {kpi.value}
              </div>
              <div style={{ fontSize: '11px', color: kpi.changeColor, marginTop: '5px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '3px' }}>
                {kpi.change}
              </div>
            </div>
          ))}
        </div>

        {/* ── Live activity + Actions ── */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: rp({ mobile: '1fr', tablet: '1fr', desktop: '1fr 1fr' }),
          gap: '14px', marginBottom: '14px',
        }}>

          {/* Live activity feed */}
          <div style={{
            background: 'var(--card-bg)', border: '1px solid var(--border)',
            borderRadius: '14px', overflow: 'hidden', boxShadow: 'var(--shadow-sm)',
          }}>
            <div style={{
              padding: '13px 16px', borderBottom: '1px solid var(--border)',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              background: 'linear-gradient(to right, #fafbfd, white)',
            }}>
              <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{
                  width: '8px', height: '8px', borderRadius: '50%',
                  background: '#22C55E', display: 'inline-block',
                  boxShadow: '0 0 0 3px rgba(34,197,94,.2)',
                  animation: 'livePulse 2s ease-in-out infinite',
                }} />
                Live activity
              </div>
              <span style={{
                fontSize: '10px', color: 'var(--text3)', fontWeight: '500',
                padding: '2px 8px', background: 'var(--surface)',
                borderRadius: '20px', border: '1px solid var(--border)',
              }}>
                Last 24h
              </span>
            </div>

            {HOME_FEED.map((item, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'flex-start', gap: '11px',
                padding: '12px 16px',
                borderBottom: i < HOME_FEED.length - 1 ? '1px solid var(--border)' : undefined,
                transition: 'background .12s',
                cursor: 'default',
              }}
                onMouseEnter={e => (e.currentTarget.style.background = 'var(--surface)')}
                onMouseLeave={e => (e.currentTarget.style.background = '')}
              >
                <div style={{
                  width: '32px', height: '32px', borderRadius: '50%', flexShrink: 0,
                  background: item.bg, color: item.color,
                  fontSize: '11px', fontWeight: '700',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  marginTop: '1px',
                  boxShadow: '0 0 0 2px rgba(255,255,255,.8), 0 1px 4px rgba(0,0,0,.1)',
                }}>
                  {item.ini}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: '13px', fontWeight: '600', color: 'var(--text)',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {item.name}{' '}
                    <span style={{ fontWeight: '400', color: 'var(--text3)' }}>{item.action}</span>
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--text3)', marginTop: '2px' }}>
                    {item.org} · {item.time}
                  </div>
                  <div style={{
                    marginTop: '6px', display: 'inline-flex', alignItems: 'center', gap: '4px',
                    padding: '3px 8px', background: item.badgeBg,
                    borderRadius: '20px', fontSize: '10px', fontWeight: '700', color: item.badgeColor,
                  }}>
                    {item.badge}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Actions queue */}
          <div style={{
            background: 'var(--card-bg)', border: '1px solid var(--border)',
            borderRadius: '14px', overflow: 'hidden', boxShadow: 'var(--shadow-sm)',
          }}>
            <div style={{
              padding: '13px 16px', borderBottom: '1px solid var(--border)',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              background: 'linear-gradient(to right, #fafbfd, white)',
            }}>
              <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text)', display: 'flex', alignItems: 'center', gap: '7px' }}>
                <i className="ti ti-bolt" style={{ fontSize: '15px', color: '#D4A042' }} />
                Actions for you
              </div>
              <span style={{
                fontSize: '10px', padding: '3px 9px',
                background: 'var(--err-bg)', color: 'var(--err)',
                borderRadius: '20px', fontWeight: '700',
                border: '1px solid var(--err-b)',
              }}>
                3 pending
              </span>
            </div>

            {HOME_ACTIONS.map((action, i) => (
              <div key={i} style={{
                padding: '13px 16px',
                borderBottom: i < HOME_ACTIONS.length - 1 ? '1px solid var(--border)' : undefined,
                transition: 'background .12s',
              }}
                onMouseEnter={e => (e.currentTarget.style.background = 'var(--surface)')}
                onMouseLeave={e => (e.currentTarget.style.background = '')}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '9px', marginBottom: '4px' }}>
                  <div style={{
                    width: '8px', height: '8px', borderRadius: '50%',
                    background: action.color, flexShrink: 0,
                    boxShadow: `0 0 0 3px ${action.color}22`,
                  }} />
                  <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text)' }}>{action.title}</div>
                </div>
                <div style={{ fontSize: '11.5px', color: 'var(--text3)', marginBottom: '10px', paddingLeft: '17px', lineHeight: 1.5 }}>
                  {action.desc}
                </div>
                <div style={{ paddingLeft: '17px', display: 'flex', gap: '7px' }}>
                  <button
                    onClick={() => go('tracking')}
                    style={{
                      padding: '6px 13px', borderRadius: '8px',
                      background: 'var(--navy)', border: 'none', color: 'white',
                      fontSize: '11.5px', fontWeight: '600', cursor: 'pointer',
                      fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: '5px',
                      transition: 'opacity .15s',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.opacity = '.88')}
                    onMouseLeave={e => (e.currentTarget.style.opacity = '1')}
                  >
                    <i className="ti ti-send" style={{ fontSize: '12px' }} /> Nudge
                  </button>
                  <button
                    onClick={() => go('analytics')}
                    style={{
                      padding: '6px 12px', borderRadius: '8px',
                      border: '1.5px solid var(--border2)', background: 'white',
                      fontSize: '11.5px', color: 'var(--text2)', cursor: 'pointer',
                      fontFamily: 'inherit', fontWeight: '500',
                      transition: 'border-color .15s',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.borderColor = 'var(--text3)')}
                    onMouseLeave={e => (e.currentTarget.style.borderColor = 'var(--border2)')}
                  >
                    Signals
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Active Playbooks ── */}
        <div style={{
          background: 'var(--card-bg)', border: '1px solid var(--border)',
          borderRadius: '14px', overflow: 'hidden', boxShadow: 'var(--shadow-sm)',
        }}>
          <div style={{
            padding: '13px 16px', borderBottom: '1px solid var(--border)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            background: 'linear-gradient(to right, #fafbfd, white)',
          }}>
            <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text)', display: 'flex', alignItems: 'center', gap: '7px' }}>
              <i className="ti ti-book-2" style={{ fontSize: '15px', color: '#6366F1' }} />
              Active Playbooks
            </div>
            <button
              onClick={() => go('admin')}
              style={{
                fontSize: '12px', color: 'var(--gold)', fontWeight: '600',
                background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                display: 'flex', alignItems: 'center', gap: '4px',
                transition: 'color .15s',
              }}
              onMouseEnter={e => (e.currentTarget.style.color = 'var(--gold2)')}
              onMouseLeave={e => (e.currentTarget.style.color = 'var(--gold)')}
            >
              View all <i className="ti ti-arrow-right" style={{ fontSize: '12px' }} />
            </button>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: rp({ mobile: '1fr', tablet: '1fr 1fr', desktop: 'repeat(3,1fr)' }),
          }}>
            {HOME_ACTIVE_PLAYBOOKS.map((pb, i) => (
              <div key={i} style={{
                padding: '14px 16px',
                borderRight: (!isMobile && i < HOME_ACTIVE_PLAYBOOKS.length - 1) ? '1px solid var(--border)' : undefined,
                borderBottom: (isMobile && i < HOME_ACTIVE_PLAYBOOKS.length - 1) ? '1px solid var(--border)' : undefined,
                transition: 'background .12s',
              }}
                onMouseEnter={e => (e.currentTarget.style.background = 'var(--surface)')}
                onMouseLeave={e => (e.currentTarget.style.background = '')}
              >
                <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text)', marginBottom: '3px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {pb.title}
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text3)', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <span>{pb.contact}</span>
                  <span style={{ color: 'var(--border2)' }}>·</span>
                  <span>{pb.org}</span>
                </div>
                {/* Progress bar */}
                <div style={{
                  background: 'var(--surface2)', borderRadius: '6px',
                  height: '6px', marginBottom: '6px', overflow: 'hidden',
                }}>
                  <div style={{
                    width: `${pb.engagement}%`, height: '100%',
                    background: pb.danger
                      ? 'linear-gradient(90deg, #EF4444, #F87171)'
                      : 'linear-gradient(90deg, #D4A042, #EAB96A)',
                    borderRadius: '6px',
                    transition: 'width .4s ease',
                  }} />
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text3)', display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontWeight: '600', color: pb.danger ? '#EF4444' : 'var(--text2)' }}>
                    {pb.engagement}%
                  </span>
                  <span>{pb.views} views</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes livePulse {
          0%, 100% { box-shadow: 0 0 0 3px rgba(34,197,94,.2); }
          50%       { box-shadow: 0 0 0 5px rgba(34,197,94,.06); }
        }
      `}</style>
    </div>
  );
}
